<template>
  <div
    class="mb-4 flex items-center bg-blue-50 border-l-4 border-blue-400 p-4 rounded"
  >
    <span v-html="INFO_ICON" class="w-6 h-6 text-blue-400 flex-shrink-0"></span>
    <span class="ml-3 text-base text-gray-700 leading-normal">
      To invite expert in bulk download the sample file and include email
      addresses in the CSV file. Upon uploading the CSV you have the option to
      customize the email message and decide if you want the signup link to go
      to your customized program landing page first or directly to the profile
      signup form. Upon upload you'll also see a validation step to ensure each
      email in the CSV is valid. After you send the invites you can check the
      expert status in the Experts tab.
    </span>
  </div>
  <div
    class="flex flex-col gap-4 w-full bg-white rounded-lg p-8 shadow max-w-3xl mx-auto mt-6"
  >
    <div>
      <h2 class="text-2xl font-semibold text-neutral-900 mb-1">
        Bulk Invite Experts
      </h2>
      <p class="text-sm text-gray-700 mb-4">
        Upload a CSV file with expert emails you want to invite.
        <button
          class="text-primary-purple underline ml-1"
          @click="downloadSampleCsv"
          type="button"
        >
          Download sample file
        </button>
      </p>
    </div>
    <!-- File Upload Card -->
    <div v-if="!fileInfo && !isProcessing" class="w-full">
      <div
        class="border-2 border-dashed border-primary-purple rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-purple-50 transition"
        @click="triggerFileInput"
        @drop.prevent="handleDrop"
        @dragover.prevent
      >
        <input
          ref="fileInput"
          type="file"
          accept=".csv,.xls,.xlsx"
          class="hidden"
          @change="handleFileChange"
        />
        <img
          src="@/assets/shared/upload-bulk.svg"
          alt="Upload"
          class="w-16 h-16 mb-2"
        />
        <span class="font-medium text-gray-700"
          >Select a CSV file to Upload</span
        >
        <span class="text-sm text-gray-500 mt-1">or drag and drop</span>
        <span class="text-xs text-gray-400 mt-2"
          >Accepted: .csv, .xls, .xlsx</span
        >
      </div>
    </div>
    <!-- Processing Spinner -->
    <div
      v-if="isProcessing"
      class="w-full flex flex-col items-center justify-center border-2 border-dashed border-primary-purple rounded-lg p-8"
    >
      <svg
        class="animate-spin h-10 w-10 text-primary-purple mb-2"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      <span class="text-gray-700">{{ processingMessage }}</span>
    </div>
    <!-- File Card + Validation Results -->
    <div v-if="fileInfo && !isProcessing" class="w-full">
      <div
        class="flex items-center bg-gray-50 border rounded-lg p-4 mb-2 relative"
      >
        <img :src="fileIcon" alt="file" class="w-10 h-10 mr-3" />
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <span class="font-medium text-gray-900">{{ fileInfo.name }}</span>
            <span class="text-xs text-gray-500">{{ fileInfo.size }}</span>
            <span class="text-xs text-gray-700 font-semibold"
              >{{ results.length }} Records</span
            >
          </div>
        </div>
        <button
          class="ml-4 text-red-500 hover:text-red-700 text-sm flex items-center"
          @click="removeFile"
        >
          <svg
            class="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          Remove
        </button>
        <span
          v-if="isValidating"
          class="absolute right-4 top-4 text-xs text-gray-500 flex items-center"
        >
          <svg
            class="animate-spin h-4 w-4 mr-1"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Validation...
        </span>
      </div>
      <div
        v-if="invalidCount > 0"
        class="bg-red-100 text-red-700 px-4 py-2 rounded mb-2 text-sm"
      >
        {{ invalidCount }} record{{ invalidCount > 1 ? "s are" : " is" }}
        invalid in the file.
      </div>
      <div
        class="border border-gray-200 rounded-lg p-2 mb-4 max-h-64 overflow-y-auto"
      >
        <ul>
          <li
            v-for="(row, idx) in results"
            :key="row.email"
            class="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-100"
          >
            <span
              class="flex-1 truncate"
              :class="{ 'text-red-600': !row.valid }"
              >{{ row.email }}</span
            >
            <template v-if="row.valid">
              <span
                class="flex items-center text-xs text-green-600 font-medium gap-1 mr-2"
              >
                <svg
                  class="w-4 h-4 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Valid
              </span>
            </template>
            <template v-else>
              <span class="text-xs text-red-500 mr-2">{{ row.error }}</span>
              <button
                @click="removeResult(idx)"
                class="text-xs text-red-500 hover:underline"
              >
                Remove
              </button>
            </template>
          </li>
        </ul>
      </div>
      <CustomMessageBox
        :message="message"
        @update:message="(val) => (message = val)"
        label-classes="font-bold text-base text-neutral-900 mb-1"
      />
      <div class="mt-4">
        <slot name="checkbox"></slot>
      </div>
      <div class="mb-4"></div>
      <div class="flex justify-between items-center mb-2">
        <button
          @click="clearResults"
          class="text-sm text-gray-500 hover:text-gray-700"
        >
          Clear Results
        </button>
        <div class="flex gap-2">
          <button
            @click="removeFile"
            class="px-4 py-2 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            @click="handleSendTestEmail"
            :disabled="sendTestEmailLoading"
            class="px-4 py-2 rounded bg-primary-purple text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="!sendTestEmailLoading">Send test Email to me</span>
            <span v-else>Sending test email...</span>
          </button>
          <button
            :disabled="validCount === 0 || isInviting"
            @click="sendInvites"
            class="px-4 py-2 rounded bg-primary-purple text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isInviting">
              <svg
                class="animate-spin h-4 w-4 mr-1 inline"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Inviting...
            </span>
            <span v-else
              >Invite {{ validCount }} Member{{
                validCount > 1 ? "s" : ""
              }}</span
            >
          </button>
        </div>
      </div>
      <div
        v-if="testEmailSuccessMessage"
        class="flex gap-2 w-full justify-center p-2.5 text-sm text-center text-green-800 bg-green-50 rounded-lg mt-2"
      >
        <span>{{ testEmailSuccessMessage }}</span>
      </div>
      <div
        v-if="testEmailErrorMessage"
        class="flex gap-2 w-full justify-center p-2.5 text-sm text-center text-red-900 bg-red-50 rounded-lg mt-2"
      >
        <span>{{ testEmailErrorMessage }}</span>
      </div>
      <div
        v-if="finalMessage"
        class="flex gap-2 w-full justify-center p-2.5 text-sm text-center text-green-800 bg-green-50 rounded-lg mt-2"
      >
        <span>{{ finalMessage }}</span>
      </div>
      <div
        v-if="finalError"
        class="flex gap-2 w-full justify-center p-2.5 text-sm text-center text-red-900 bg-red-50 rounded-lg mt-2"
      >
        <span>{{ finalError }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { gql } from "@apollo/client/core";
import { useMutation } from "@vue/apollo-composable";
import CustomMessageBox from "./CustomMessageBox.vue";
import { useUserStore } from "@/stores/user";
import { useTestInvitation } from "@/composables/useTestInvitation";
import { INFO_ICON } from "@/assets/svg/shared/svg-constants";

const BULK_INVITE_SEND = gql`
  mutation bulkInviteSend($input: BulkInviteEmailInput!) {
    bulkInviteSend(input: $input) {
      success
      message
      emailCount
    }
  }
`;

function formatFileSize(size: number) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

function getFileIcon(name: string) {
  if (name.endsWith(".csv"))
    return "https://img.icons8.com/color/48/000000/csv.png";
  if (name.endsWith(".xls") || name.endsWith(".xlsx"))
    return "https://img.icons8.com/color/48/000000/ms-excel.png";
  return "https://img.icons8.com/ios-filled/50/000000/document--v1.png";
}

interface LocalResult {
  email: string;
  valid: boolean;
  error?: string;
}

export default defineComponent({
  name: "InviteBulkSection",
  components: { CustomMessageBox },
  props: {
    programId: { type: String, required: true },
    companyId: { type: String, required: false },
    invitationType: { type: String, required: true },
    directSignup: { type: Boolean, required: false, default: false },
  },
  emits: ["invites-sent"],
  setup(props, { emit }) {
    const fileInput = ref<HTMLInputElement>();
    const fileInfo = ref<{ name: string; size: string } | null>(null);
    const fileIcon = ref("");
    const results = ref<LocalResult[]>([]);
    const isProcessing = ref(false);
    const isValidating = ref(false);
    const isInviting = ref(false);
    const finalMessage = ref("");
    const finalError = ref("");
    const processingMessage = ref("Uploading File...");
    const message = ref("");
    const userStore = useUserStore();
    const {
      sendTestEmail,
      sendTestEmailLoading,
      finalMessage: testEmailSuccessMessage,
      finalError: testEmailErrorMessage,
    } = useTestInvitation();

    onMounted(() => {
      const programName = userStore.selectedProgram?.name || "your company";
      message.value = `<strong>${programName}</strong> is using SlashExperts and has invited you to join their expert program.<br><br><strong>You are being invited because:<br></strong><ul><li><strong>${programName}</strong> values your expertise</li><li>You have experience using <strong>${programName}</strong></li><li><strong>${programName}</strong> wants to pay you to occasionally speak with peers interested in discussing <strong>${programName}</strong></li></ul>`;
    });

    const validEmails = computed(() =>
      results.value.filter((r) => r.valid).map((r) => r.email)
    );
    const validCount = computed(() => validEmails.value.length);
    const invalidCount = computed(
      () => results.value.filter((r) => !r.valid).length
    );

    const triggerFileInput = () => fileInput.value?.click();
    const handleDrop = (event: DragEvent) => {
      const files = event.dataTransfer?.files;
      if (files && files.length > 0) processFile(files[0]);
    };
    const handleFileChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) processFile(target.files[0]);
    };
    const processFile = async (file: File) => {
      fileInfo.value = null;
      results.value = [];
      finalMessage.value = "";
      finalError.value = "";
      isProcessing.value = true;
      processingMessage.value = "Uploading File...";
      setTimeout(async () => {
        try {
          fileInfo.value = { name: file.name, size: formatFileSize(file.size) };
          fileIcon.value = getFileIcon(file.name);
          processingMessage.value = "Validation...";
          isValidating.value = true;
          // Only support CSV for now
          let text = "";
          if (file.name.endsWith(".csv")) {
            text = await file.text();
          } else {
            throw new Error("Only CSV files are supported for now.");
          }
          const lines = text.split("\n").filter((line) => line.trim());
          if (lines.length === 0) throw new Error("CSV file is empty.");
          const dataLines = lines[0].toLowerCase().includes("email")
            ? lines.slice(1)
            : lines;
          const emails = dataLines
            .map((line) => line.trim().split(",")[0]?.trim())
            .filter((email) => email && email.length > 0);
          if (emails.length === 0)
            throw new Error("No valid emails found in CSV file.");
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          const seen = new Set<string>();
          results.value = emails.map((email) => {
            if (!emailRegex.test(email))
              return { email, valid: false, error: "Invalid email format" };
            if (seen.has(email))
              return { email, valid: false, error: "Duplicate email" };
            seen.add(email);
            return { email, valid: true };
          });
        } catch (err: unknown) {
          if (err instanceof Error) {
            finalError.value = err.message;
          } else {
            finalError.value = "Error reading file.";
          }
          fileInfo.value = null;
          results.value = [];
        } finally {
          isProcessing.value = false;
          isValidating.value = false;
        }
      }, 800); // Simulate upload/validation delay
    };
    const removeResult = (idx: number) => results.value.splice(idx, 1);
    const clearResults = () => {
      results.value = [];
      finalMessage.value = "";
      finalError.value = "";
    };
    const removeFile = () => {
      fileInfo.value = null;
      results.value = [];
      finalMessage.value = "";
      finalError.value = "";
    };
    const downloadSampleCsv = () => {
      const csvContent = "email\nexample@email.com";
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sample_invites.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    };
    const { mutate: sendBulkInvites } = useMutation(BULK_INVITE_SEND);
    const sendInvites = async () => {
      if (validCount.value === 0) return;
      isInviting.value = true;
      finalMessage.value = "";
      finalError.value = "";
      try {
        const response = await sendBulkInvites({
          input: {
            bulkInviteParams: {
              emails: validEmails.value,
              programId: props.programId,
              companyId: props.companyId,
              message: message.value,
              inviteType: props.invitationType,
              directSignup: props.directSignup,
            },
          },
        });

        if (response?.data?.bulkInviteSend?.success) {
          finalMessage.value =
            response.data.bulkInviteSend.message ||
            `Successfully sent ${validCount.value} invites!`;
          emit("invites-sent", {
            success: true,
            message: response.data.bulkInviteSend.message,
            emailCount: response.data.bulkInviteSend.emailCount,
          });
        } else {
          finalError.value =
            response?.data?.bulkInviteSend?.message ||
            "Failed to send invites.";
        }
      } catch (error: unknown) {
        if (error instanceof Error) {
          finalError.value = error.message;
        } else {
          finalError.value = "An error occurred while sending invites.";
        }
      } finally {
        isInviting.value = false;
      }
    };
    const handleSendTestEmail = () => {
      sendTestEmail({
        programId: props.programId || "",
        message: message.value,
        userEmail: userStore.email || "",
      });
    };
    return {
      fileInput,
      fileInfo,
      fileIcon,
      results,
      isProcessing,
      isValidating,
      isInviting,
      finalMessage,
      finalError,
      processingMessage,
      validEmails,
      validCount,
      invalidCount,
      triggerFileInput,
      handleDrop,
      handleFileChange,
      processFile,
      removeResult,
      clearResults,
      removeFile,
      downloadSampleCsv,
      sendInvites,
      message,
      handleSendTestEmail,
      sendTestEmailLoading,
      testEmailSuccessMessage,
      testEmailErrorMessage,
      INFO_ICON,
    };
  },
});
</script>
